"use client";

import { createContext, useContext, useState, ReactNode, useMemo } from "react";

type LoanData = {
    amount: number;
    termYears: number;
    interestRate: number;
    yearsPaid?: number;
};

type TaxesInsurance = {
    annualPropertyTax: number;
    annualInsurance: number;
};

type HomeInfoData = {
    appraisedValue: number;
    expectedYears: number;
};

type RefinanceContextType = {
    currentLoan: LoanData;
    setCurrentLoan: (data: LoanData) => void;
    refiLoan: LoanData;
    setRefiLoan: (data: LoanData) => void;
    taxesInsurance: TaxesInsurance;
    setTaxesInsurance: (data: TaxesInsurance) => void;
    homeInfo: HomeInfoData;
    setHomeInfo: (data: HomeInfoData) => void;
};

const defaultLoan: LoanData = { amount: 0, termYears: 0, interestRate: 0 };
const defaultTaxes: TaxesInsurance = { annualPropertyTax: 0, annualInsurance: 0 };
const defaultHomeInfo: HomeInfoData = { appraisedValue: 0, expectedYears: 1 };

const RefinanceContext = createContext<RefinanceContextType | undefined>(undefined);

export const RefinanceProvider = ({ children }: { children: ReactNode }) => {
    const [currentLoan, setCurrentLoan] = useState<LoanData>(defaultLoan);
    const [refiLoan, setRefiLoan] = useState<LoanData>(defaultLoan);
    const [taxesInsurance, setTaxesInsurance] = useState<TaxesInsurance>(defaultTaxes);
    const [homeInfo, setHomeInfo] = useState<HomeInfoData>(defaultHomeInfo);

    const value = useMemo(
        () => ({
            currentLoan,
            setCurrentLoan,
            refiLoan,
            setRefiLoan,
            taxesInsurance,
            setTaxesInsurance,
            homeInfo,
            setHomeInfo,
        }),
        [currentLoan, refiLoan, taxesInsurance, homeInfo],
    );

    return <RefinanceContext.Provider value={value}>{children}</RefinanceContext.Provider>;
};

export const useRefinanceContext = () => {
    const context = useContext(RefinanceContext);
    if (!context) {
        throw new Error("useRefinanceContext must be used within a RefinanceProvider");
    }
    return context;
};
